import { useRef, useState } from 'react';
import { authClient } from '@/lib/authClient';
import { hydrateFromServer } from '@/lib/bootstrap';
import { api } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * When true, gates OTP send on the email having a coach or operator role.
   * Use this on platform.* login surfaces where only platform users belong.
   */
  platformMode?: boolean;
}

type Step = 'email' | 'sending' | 'otp' | 'verifying' | 'error';

export function LoginDialog({ open, onOpenChange, platformMode }: Props) {
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState<Step>('email');
  const [error, setError] = useState('');
  const otpRef = useRef<HTMLInputElement>(null);

  async function handleSendOtp() {
    if (!email.trim()) return;
    setStep('sending');
    setError('');

    if (platformMode) {
      try {
        const { hasRole } = await api.coach.platformRoleCheck(email.trim());
        if (!hasRole) {
          setError('NO_COACH_ROLE');
          setStep('error');
          return;
        }
      } catch {
        setError('Vorab-Prüfung fehlgeschlagen. Bitte versuche es erneut.');
        setStep('error');
        return;
      }
    }

    const { error } = await authClient.emailOtp.sendVerificationOtp({
      email: email.trim(),
      type: 'sign-in',
    });
    if (error) {
      setError('Code konnte nicht gesendet werden. Bitte versuche es erneut.');
      setStep('error');
    } else {
      setStep('otp');
      setTimeout(() => otpRef.current?.focus(), 50);
    }
  }

  async function handleVerifyOtp() {
    if (otp.length < 6) return;
    setStep('verifying');
    setError('');
    const { data, error } = await authClient.signIn.emailOtp({
      email: email.trim(),
      otp: otp.trim(),
    });
    if (error || !data?.user) {
      setError('Ungültiger oder abgelaufener Code. Bitte neu anfordern.');
      setStep('error');
    } else {
      await hydrateFromServer(data.user);
      handleClose(false);
    }
  }

  function handleClose(isOpen: boolean) {
    if (!isOpen) {
      setEmail('');
      setOtp('');
      setStep('email');
      setError('');
    }
    onOpenChange(isOpen);
  }

  const isLoading = step === 'sending' || step === 'verifying';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Anmelden</DialogTitle>
          <DialogDescription>
            {step === 'otp' || step === 'verifying' || (step === 'error' && otp)
              ? `Code wurde an ${email} gesendet.`
              : 'Melde dich an, um deine Daten zu sichern und auf anderen Geräten zuzugreifen.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Step 1: Email */}
          {(step === 'email' || step === 'sending') && (
            <div className="space-y-2">
              <Input
                type="email"
                placeholder="deine@email.de"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSendOtp()}
                disabled={isLoading}
                autoFocus
              />
              <Button
                className="w-full"
                onClick={handleSendOtp}
                disabled={!email.trim() || isLoading}
              >
                {step === 'sending' ? 'Senden…' : 'Code senden'}
              </Button>
            </div>
          )}

          {/* Step 2: OTP */}
          {(step === 'otp' || step === 'verifying') && (
            <div className="space-y-2">
              <Input
                ref={otpRef}
                type="text"
                inputMode="numeric"
                placeholder="6-stelliger Code"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                onKeyDown={(e) => e.key === 'Enter' && handleVerifyOtp()}
                disabled={isLoading}
                className="tracking-widest text-center text-lg"
                maxLength={6}
              />
              <Button
                className="w-full"
                onClick={handleVerifyOtp}
                disabled={otp.length < 6 || isLoading}
              >
                {step === 'verifying' ? 'Prüfen…' : 'Anmelden'}
              </Button>
              <button
                className="w-full text-xs text-muted-foreground underline underline-offset-2"
                onClick={() => { setStep('email'); setOtp(''); setError(''); }}
              >
                Anderen Code anfordern
              </button>
            </div>
          )}

          {/* Error state */}
          {step === 'error' && (
            <div className="space-y-3">
              {error === 'NO_COACH_ROLE' ? (
                <div className="space-y-2 text-sm">
                  <p className="text-destructive">
                    Diese E-Mail hat keinen Coach-Zugang.
                  </p>
                  <p className="text-muted-foreground">
                    Wolltest du dich vielleicht für das Inneres-Team-Tool anmelden?
                    Dann geht's hier weiter:{' '}
                    <a
                      href="https://app.teamity.io"
                      className="text-primary underline underline-offset-2"
                    >
                      app.teamity.io
                    </a>
                    .
                  </p>
                </div>
              ) : (
                <p className="text-sm text-destructive">{error}</p>
              )}
              <Button
                variant="outline"
                className="w-full"
                onClick={() => { setStep('email'); setOtp(''); setError(''); }}
              >
                Nochmal versuchen
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
